import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { taskKeys } from '../query-keys';
import { useAuthStore } from '@/stores/auth-store';
import {
  listTasksContract,
  type ListTasksResponse,
  type ProcessStepAssignment,
  type InProgressRecording,
  type Process as TaskProcess,
  type Survey,
  type TaskListItem
} from '@bliks-io/contracts';

// Re-export types for convenience
export type { ProcessStepAssignment, InProgressRecording, TaskProcess, Survey };

/**
 * Total actionable items across all task sections. Single source of truth for
 * the sidebar badge, page title count, and empty-state check — a new task
 * section only needs to be added here, not in every consumer.
 *
 * `openTasks` are the Aufgaben from `useMyOpenTasks`, a separate read: they
 * count only when the caller passes them, so a surface without that query
 * still counts its process work.
 */
export function countTasks(
  tasks: ListTasksResponse | undefined,
  openTasks?: TaskListItem[] | undefined
): number {
  const openTaskCount = openTasks?.length ?? 0;
  if (!tasks) return openTaskCount;
  return (
    (tasks.openProcesses?.length ?? 0) +
    (tasks.assignments?.length ?? 0) +
    (tasks.inProgressRecordings?.length ?? 0) +
    (tasks.pendingSurveys?.length ?? 0) +
    (tasks.entitySuggestions?.length ?? 0) +
    openTaskCount
  );
}

/**
 * Fetch all tasks for the current user
 * Returns open processes (not yet started recording), pending process step assignments,
 * in-progress recordings, and pending surveys
 *
 * @example
 * ```tsx
 * function MyTasksPage() {
 *   const { data: tasks, isLoading } = useTasks();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!tasks) return null;
 *
 *   return (
 *     <>
 *       <OpenProcessesTable processes={tasks.openProcesses} />
 *       <InProgressRecordingsTable recordings={tasks.inProgressRecordings} />
 *       <AssignmentsTable assignments={tasks.assignments} />
 *       <PendingSurveysTable surveys={tasks.pendingSurveys} />
 *     </>
 *   );
 * }
 * ```
 */
export function useTasks() {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: taskKeys.all,
    queryFn: async ({ signal }): Promise<ListTasksResponse> => {
      const { data } = await apiClient.get<ListTasksResponse>(listTasksContract.path, { signal });
      return data;
    },
    enabled: !!token,
    // Focus, mount and the mutation invalidations keep the badge fresh on every
    // user-visible event; the timer only covers OTHER users' assignments, so it
    // is deliberately slow (a 30 s poll was the largest non-LLM load in prod).
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 120000,
    refetchIntervalInBackground: false
  });
}
